// What a card and a heading say. Pure functions of the task, the day the card
// sits on and the clock, shared by the Plan tab, the Calendar and search.

import type { DateStr, Recurrence, Task } from '@/types'
import { addDays, fmtDate, fmtWeekSpan, parts, weekdayName } from './dates'
import { dueUrgency, followUpUrgency, placeDay, routineWeek, type Now, type Urgency } from './status'

/** "Fri 16", the short date the details line uses. */
export function fmtShort(d: DateStr): string {
  return `${weekdayName(d)} ${parts(d).d}`
}

export interface DateLine {
  text: string
  /** Orange when soon, red when late, plain otherwise. */
  tone: Urgency
}

/**
 * The date part of a card's details line. On a day the date is dropped: only a
 * time ("Due 10:00"), a different due date ("Due Fri 16 17:00"), or "Due
 * today" / "Due tomorrow" while it is close. In Not done: "Overdue, Thu 8
 * Oct", "Follow up, Mon 12 Oct", "Didn't do it, Mon 12", or just "Didn't do
 * it" for a routine. Null when there is nothing to say.
 */
export function dateLine(t: Task, where: { day?: DateStr | null; notDone?: boolean }, now: Now): DateLine | null {
  const due = dueUrgency(t, now)
  const follow = followUpUrgency(t, now)
  if (where.notDone) {
    if (t.dueDate && due === 'late') return { text: `Overdue, ${fmtDate(t.dueDate)}`, tone: 'late' }
    if (t.waiting && t.followUpDate && follow === 'late') return { text: `Follow up, ${fmtDate(t.followUpDate)}`, tone: 'late' }
    if (t.kind === 'routine') return { text: "Didn't do it", tone: null }
    const day = placeDay(t, now.date)
    return { text: day ? `Didn't do it, ${fmtShort(day)}` : "Didn't do it", tone: null }
  }
  const day = where.day ?? null
  if (t.waiting && t.followUpDate && t.followUpDate === day) {
    if (t.followUpTime) return { text: `Follow up ${t.followUpTime}`, tone: follow }
    if (follow) return { text: day === now.date ? 'Follow up today' : 'Follow up tomorrow', tone: follow }
  }
  if (!t.dueDate || t.kind === 'routine') return null
  if (t.dueDate === day) {
    if (t.dueTime) return { text: due === 'late' ? `Overdue ${t.dueTime}` : `Due ${t.dueTime}`, tone: due }
    if (due === 'soon') return { text: day === now.date ? 'Due today' : 'Due tomorrow', tone: due }
    if (due === 'late') return { text: 'Overdue', tone: due }
    return null
  }
  // off its due day (on another day, or in No date): today and tomorrow by name, as on the day itself
  const when = t.dueDate === now.date ? 'today' : t.dueDate === addDays(now.date, 1) ? 'tomorrow' : fmtShort(t.dueDate)
  const text = `Due ${when}${t.dueTime ? ` ${t.dueTime}` : ''}`
  if (due !== 'late') return { text, tone: due }
  return { text: t.dueDate === now.date ? `Overdue ${t.dueTime ?? ''}`.trim() : `Overdue, ${fmtShort(t.dueDate)}${t.dueTime ? ` ${t.dueTime}` : ''}`, tone: due }
}

const FREQ_WORD = { day: 'day', week: 'week', month: 'month', year: 'year' } as const
const FREQ_PLAIN = { day: 'Daily', week: 'Weekly', month: 'Monthly', year: 'Yearly' } as const

/** "Weekly", "Weekdays", "Every 2 weeks", "Every 3 days", "Monthly", "Yearly". */
export function repeatLabel(r: Recurrence | null): string {
  if (!r) return ''
  if (r.freq === 'week' && r.interval === 1 && r.weekdays && r.weekdays.length === 5 && [1, 2, 3, 4, 5].every((d) => r.weekdays!.includes(d))) {
    return 'Weekdays'
  }
  if (r.interval === 1) return FREQ_PLAIN[r.freq]
  return `Every ${r.interval} ${FREQ_WORD[r.freq]}s`
}

/** The kind word on a card: "Routine", or a repeat's cadence. */
export function kindLabel(t: Pick<Task, 'kind' | 'recurrence'>): string {
  if (t.kind === 'routine') return 'Routine'
  if (t.kind === 'repeat') return repeatLabel(t.recurrence)
  return ''
}

/** A search result's dates: "Do it on Thu 15 Oct · Due Fri 16 Oct", "Routine, not placed", "Done Mon 12 Oct". */
export function datesSummary(t: Task, today: DateStr): string {
  if (t.kind === 'once' && t.done) return t.doneOn ? `Done ${fmtDate(t.doneOn, t.doneOn.slice(0, 4) !== today.slice(0, 4))}` : 'Done'
  const withYear = (d: DateStr) => fmtDate(d, d.slice(0, 4) !== today.slice(0, 4))
  const bits: string[] = []
  if (t.kind === 'routine') {
    const r = routineWeek(t, today)
    bits.push(r.plannedDate ? `Routine, ${withYear(r.plannedDate)}` : 'Routine, not placed')
  } else if (t.plannedDate) {
    bits.push(`Do it on ${withYear(t.plannedDate)}`)
  }
  if (t.dueDate && t.kind !== 'routine') bits.push(`Due ${withYear(t.dueDate)}${t.dueTime ? ` ${t.dueTime}` : ''}`)
  if (t.waiting) bits.push(t.followUpDate ? `Waiting, follow up ${withYear(t.followUpDate)}` : 'Waiting')
  if (!bits.length) bits.push('No date')
  return bits.join(' · ')
}

/** "Today, Wed 14 Oct", "Tomorrow, Thu 15 Oct", else "Fri 16 Oct"; short drops the month: "Today, Wed 14". */
export function dayHeading(d: DateStr, today: DateStr, short = false): string {
  const date = short ? fmtShort(d) : fmtDate(d)
  if (d === today) return `Today, ${date}`
  if (d === addDays(today, 1)) return `Tomorrow, ${date}`
  return date
}

/** The week's name and dates: "This week" over "12 – 18 Oct"; other weeks just the dates. */
export function weekTitle(weekStart: DateStr, todayMonday: DateStr): { name: string | null; span: string } {
  const span = fmtWeekSpan(weekStart, addDays(weekStart, 6))
  const name = weekStart === todayMonday ? 'This week' : weekStart === addDays(todayMonday, 7) ? 'Next week' : weekStart === addDays(todayMonday, -7) ? 'Last week' : null
  return { name, span }
}

/** The Calendar's Day label: "Today", "Tomorrow" or "Yesterday" over the date, or just the date. */
export function dayTitle(d: DateStr, today: DateStr): { name: string | null; date: string } {
  const name = d === today ? 'Today' : d === addDays(today, 1) ? 'Tomorrow' : d === addDays(today, -1) ? 'Yesterday' : null
  return { name, date: fmtDate(d) }
}

/** A person's mark on a card: one initial, or two letters when both names start alike. */
export function initialsFor(names: Record<string, string>): Record<string, string> {
  const entries = Object.entries(names)
  const firstLetters = entries.map(([, n]) => (n.trim()[0] ?? '?').toUpperCase())
  const clash = new Set(firstLetters).size < firstLetters.length
  const out: Record<string, string> = {}
  entries.forEach(([uid, n], i) => {
    const t = n.trim()
    out[uid] = clash ? (t.slice(0, 2) || '?').replace(/^./, (c) => c.toUpperCase()) : firstLetters[i]
  })
  return out
}

/** Steps done over steps in all, as "3/7". */
export function checklistProgress(t: Pick<Task, 'checklist'>): string {
  const steps = t.checklist ?? []
  if (!steps.length) return ''
  return `${steps.filter((s) => s.done).length}/${steps.length}`
}
